import { DocumentNode } from "graphql";
import { Customer } from "../graphql/types";
import { Product } from "@snippets/types";

/**
 * Gets the value of a specified cookie.
 * @param cookieName The name of the cookie to retrieve.
 * @returns The value of the cookie if it exists, otherwise null.
 */
export function getCookie(cookieName: string): string | null {
  const cookies = document.cookie.split("; ");
  const targetCookie = cookies.find((cookie) =>
    cookie.startsWith(`${cookieName}=`),
  );
  return targetCookie ? decodeURIComponent(targetCookie.split("=")[1]) : null;
}

/**
 * Sets a cookie with specified options.
 * @param cookieName The name of the cookie.
 * @param value The value to set for the cookie.
 * @param days Optional. The number of days until the cookie expires. Defaults to 365 days.
 * @param path Optional. The path where the cookie is accessible. Defaults to the entire domain.
 * @param domain Optional. The domain where the cookie is accessible.
 * @param secure Optional. If true, the cookie will only be sent over secure (HTTPS) connections.
 * @param sameSite Optional. Controls how cookies are sent with cross-site requests. Defaults to "Lax".
 */
export function setCookie(
  cookieName: string,
  value: string,
  days: number = 365,
  path: string = "/",
  domain?: string,
  secure: boolean = false,
  sameSite: "Lax" | "Strict" | "None" = "Lax",
): void {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  let cookieString = `${cookieName}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=${path}`;

  if (domain) {
    cookieString += `; domain=${domain}`;
  }
  if (secure) {
    cookieString += "; secure";
  }
  cookieString += `; SameSite=${sameSite}`;
  document.cookie = cookieString;
}

/**
 * Gets the users browsing context.
 * @returns {Promise} - The response from the fetch request.
 */
export async function getUsersBrowsingLocale() {
  try {
    return await fetch(
      window.Shopify.routes.root +
        "browsing_context_suggestions.json" +
        "?country[enabled]=true" +
        `&country[exclude]=${window.Shopify.country}` +
        "&language[enabled]=true" +
        `&language[exclude]=${window.Shopify.locale}`,
    ).then((r) => r.json());
    //eslint-disable-next-line
  } catch (error: any) {
    throw new Error(error);
  }
}

// Utility function to remove duplicates from an array
export const makeArrayUnique = <T>(array: T[]): T[] =>
  array.filter((item, index) => array.indexOf(item) === index);

// Converts a string to a URL slug
export const handleize = (string: string): string =>
  string
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export interface FormParams {
  [key: string]: string | string[] | Record<string, string>;
}

// Extracts parameters from a form and returns them as an object
export const getParamsFromForm = (form: HTMLFormElement): FormParams => {
  const formData = new FormData(form);
  const params: FormParams = {};
  for (const [key, value] of formData.entries()) {
    if (key.includes("[]")) {
      const arrayKey = key.replace("[]", "");
      if (!params[arrayKey]) {
        params[arrayKey] = [];
      }
      (params[arrayKey] as string[]).push(value as string);
    } else {
      params[key] = value as string;
    }
  }
  return params;
};

// Generates a query string from form data
export const getQueryFromForm = (
  form: HTMLFormElement,
  prefix = "",
): string => {
  const params = getParamsFromForm(form);
  const queryString = Object.keys(params)
    .map((key) => {
      const value = params[key];
      if (Array.isArray(value)) {
        return value
          .map((item) => `${key}[]=${encodeURIComponent(item)}`)
          .join("&");
      } else if (typeof value === "object") {
        return Object.keys(value)
          .map(
            (subKey) =>
              `${key}[${subKey}]=${encodeURIComponent(value[subKey])}`,
          )
          .join("&");
      } else {
        return `${key}=${encodeURIComponent(value)}`;
      }
    })
    .join("&");
  return `${prefix}${queryString}`;
};

// Converts a simple object to a query string
export const objectToQueryString = (
  object: Record<string, string | number>,
  prefix = "",
): string =>
  prefix +
  Object.keys(object)
    .map((key) => `${key}=${encodeURIComponent(object[key])}`)
    .join("&");

// Generates modal HTML markup with custom styles
export const generateModal = (
  outerClass: string,
  innerContent: string,
): string => `
  <div class="${outerClass}">
    <div class="${outerClass}__inner">
      ${innerContent}
    </div>
    <style>
      body {
        overflow: hidden;
      }
      .${outerClass} {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        z-index: 9;
        display: flex;
        justify-content: center;
        align-items: center;
      }
      .${outerClass}__inner {
        width: 800px;
        max-width: 96%;
        height: auto;
        min-height: 250px;
        max-height: calc(100vh - 100px);
        background-color: #fff;
        border-radius: 4px;
        overflow-y: auto;
        overflow-x: hidden;
        box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
        z-index: 9;
      }
    </style>
  </div>
`;

// Re-adds script tags to ensure they execute when the element content changes
export const reloadScripts = (element: HTMLElement): boolean => {
  if (!element) return false;
  const scriptTags = element.querySelectorAll("script");
  scriptTags.forEach((oldScript) => {
    const newScript = document.createElement("script");
    newScript.textContent = oldScript.textContent;
    oldScript.parentNode?.replaceChild(newScript, oldScript);
  });
  return true;
};

type ValidInput = string | string[] | Record<string, unknown>;

// Checks if a given value (string, array, or object) is "empty"
export const isEmpty = (value: ValidInput): boolean => {
  if (Array.isArray(value)) {
    return value.length === 0;
  } else if (typeof value === "object" && value !== null) {
    return Object.keys(value).length === 0;
  } else {
    return value === "";
  }
};

interface Variant {
  id: string;
  featured_media?: { position?: number };
}

// Calculates image indexes for variants based on the position of featured media
export const getVariantImageIndexes = (
  variants: Variant[],
): Record<string, { start: number; end: number }> => {
  const indexes: Record<string, { start: number; end: number }> = {};
  variants.forEach((variant, index) => {
    const start = variant.featured_media?.position || 0;
    const end =
      variants[index + 1]?.featured_media?.position || variants.length;
    indexes[variant.id] = { start, end };
  });
  return indexes;
};

export async function fetchGraphQL<TData, TVariables>(
  query: DocumentNode,
  variables: TVariables,
  url?: string,
): Promise<TData> {
  const queryUrl =
    url ||
    `https://${window.themeVars.store.permanent_domain}/api/${window.themeVars.config.storefrontApiVersion}/graphql`;
  try {
    const response = await fetch(queryUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Shopify-Storefront-Access-Token":
          window.themeVars.config.storefrontAccessToken,
      },
      body: JSON.stringify({
        query: query.loc!.source.body,
        variables,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const json = await response.json();
    if (json.errors) {
      console.error(json.errors);
      throw new Error("GraphQL query error!");
    }

    return json.data as TData;
  } catch (error) {
    console.error("Error in fetchGraphQL:", error);
    throw error;
  }
}

//eslint-disable-next-line
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): T {
  let timeoutId: number | undefined;

  return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => func.apply(this, args), wait);
  } as T;
}

export function isMobile(): boolean {
  return window.innerWidth <= 768;
}

// [TODO] Fix this any type
// tslint:disable-next-line: no-any
// eslint-disable-next-line
export const removeEmptyKeys = <T extends Record<string, any>>(
  object: T,
): T => {
  const keys = Object.keys(object) as Array<keyof T>;
  keys.forEach((key) => {
    if (
      object[key] === null ||
      object[key] === undefined ||
      isEmpty(object[key])
    ) {
      delete object[key];
    }
  });
  return object;
};

// eslint-disable-next-line
export function isAuthorized(product: any, customer: any) {
  const accessRestriction = product.accessRestriction;
  // Returned truthy without checking for customer data. Used ID - Phil
  const isLoggedIn = customer && customer.id;
  const isEfpMember = customer?.tags?.includes("efp-member");
  const isPbMember = customer?.tags?.includes("pb-member");

  switch (accessRestriction) {
    case "all":
      return isLoggedIn;
    case "efp":
      return isEfpMember;
    case "pb":
      return isPbMember;
    case "efp_pb":
      return isEfpMember && isPbMember;
    default:
      return true;
  }
}
